import { useLanguage, t } from "../../../i18n";
import React, { useState } from "react";
import { Container, Row, Col, Card, Button, Form, Alert, Spinner } from "react-bootstrap";
import { Formik } from "formik";
import * as Yup from "yup";
import { Link, useNavigate } from "react-router-dom";
import Logo from "../../../components/common/Logo/Logo";
import ROUTES from "../../../config/routes";
import registerUser from "../../../features/auth/api/registerUser";
import useDocumentTitle from "../../../hooks/useDocumentTitle";
import { getApiValidationErrors, getRegisterErrorMessage } from "../../../utils/apiErrorHandler";
import "./Register.css";
const initialValues = {
  fullName: "",
  email: "",
  mobileNumber: "",
  acceptTerms: false
};
const validationSchema = Yup.object({
  fullName: Yup.string().matches(/^[\p{L}\p{M}\s]+$/u, "Full Name should contain only alphabets and spaces").min(3, "Full Name must be at least 3 characters").max(25, "Full Name cannot exceed 25 characters").required("Full Name is required"),
  email: Yup.string().email("Enter a valid email address").required("Email Address is required"),
  mobileNumber: Yup.string().matches(/^05\d{8}$/, "Mobile Number must start with 05 and be 10 digits").required("Mobile Number is required"),
  acceptTerms: Yup.boolean().oneOf([true], "You must accept the terms")
});
const backendToFormikFieldMap = {
  full_name: "fullName",
  email: "email",
  mobile: "mobileNumber",
  acceptTerms: "acceptTerms"
};
export default function Register() {
  useLanguage();
  useDocumentTitle("Register");
  const navigate = useNavigate();
  const [apiError, setApiError] = useState("");
  const handleSubmit = async (values, {
    setSubmitting,
    setFieldError,
    resetForm
  }) => {
    setApiError("");
    try {
      const response = await registerUser(values);
      const isSuccessful = response?.success === true || response?.status === 200 || response?.status === 201 || response?.message || response?.data || response?.user || response?.token || response && typeof response === "object" && !("success" in response);
      if (isSuccessful) {
        resetForm();
        navigate(ROUTES.LOGIN, {
          state: {
            email: values.email,
            fromRegister: true
          }
        });
      } else {
        setApiError("We could not create your account right now. Please try again.");
      }
    } catch (error) {
      const validationErrors = getApiValidationErrors(error);
      if (Object.keys(validationErrors).length > 0) {
        Object.entries(validationErrors).forEach(([field, message]) => {
          const formikField = backendToFormikFieldMap[field] || field;
          setFieldError(formikField, Array.isArray(message) ? message[0] : message);
        });
      } else {
        setApiError(getRegisterErrorMessage(error));
      }
    } finally {
      setSubmitting(false);
    }
  };
  return <div className="bb-auth-page">
      <Container>
        <Row className="justify-content-center">
          <Col lg={6} xl={5}>
            <div className="bb-auth-page__brand">
              <div className="bb-auth-logo">
                <Logo />
              </div>
            </div>

            <Card className="bb-auth-card">
              <Card.Body>
                <div className="bb-auth-card__header">
                  <h1 className="bb-auth-card__title">{t("Create Your Account")}</h1>
                  <p className="bb-auth-card__subtitle">{t("Register to begin your BaytBuddy journey and continue with profile completion.")}

                  </p>
                </div>

                {apiError ? <Alert variant="danger" className="mb-3">
                    {t(apiError)}
                  </Alert> : null}

                <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>

                  {({
                  values,
                  errors,
                  touched,
                  handleChange,
                  handleBlur,
                  handleSubmit,
                  setFieldValue,
                  isSubmitting
                }) => <Form onSubmit={handleSubmit}>
                      <Row className="g-4">
                        <Col xs={12}>
                          <Form.Group>
                            <Form.Label>{t("Full Name")}</Form.Label>
                            <Form.Control type="text" name="fullName" value={values.fullName} onChange={handleChange} onBlur={handleBlur} isInvalid={touched.fullName && !!errors.fullName} placeholder={t("Enter your full name")} />

                            <Form.Control.Feedback type="invalid">
                              {t(errors.fullName)}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>

                        <Col xs={12}>
                          <Form.Group>
                            <Form.Label>{t("Email Address")}</Form.Label>
                            <Form.Control type="email" name="email" value={values.email} onChange={handleChange} onBlur={handleBlur} isInvalid={touched.email && !!errors.email} placeholder={t("Enter your email address")} />

                            <Form.Control.Feedback type="invalid">
                              {t(errors.email)}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>

                        <Col xs={12}>
                          <Form.Group>
                            <Form.Label>{t("Mobile Number")}</Form.Label>
                            <Form.Control type="tel" inputMode="numeric" name="mobileNumber" value={values.mobileNumber} onChange={handleChange} onBlur={handleBlur} isInvalid={touched.mobileNumber && !!errors.mobileNumber} placeholder={t("e.g. 05XXXXXXXX")} />

                            <Form.Control.Feedback type="invalid">
                              {t(errors.mobileNumber)}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>

                        <Col xs={12}>
                          <Form.Group>
                            <Form.Check type="checkbox" id="acceptTerms" name="acceptTerms" label={t("I accept the Terms and Conditions")} checked={values.acceptTerms} onChange={e => setFieldValue("acceptTerms", e.target.checked)} isInvalid={touched.acceptTerms && !!errors.acceptTerms} />

                            {touched.acceptTerms && errors.acceptTerms ? <div className="bb-auth-card__check-error">
                                {t(errors.acceptTerms)}
                              </div> : null}
                          </Form.Group>
                        </Col>

                        <Col xs={12}>
                          <Button type="submit" className="w-100 bb-auth-card__submit-btn" disabled={isSubmitting}>

                            {isSubmitting ? <>
                                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />{t("Registering...")}


                          </> : t("Register")}
                          </Button>
                        </Col>
                      </Row>
                    </Form>}
                </Formik>

                <div className="bb-auth-card__footer">
                  <div className="mb-2">
                    <Button type="button" variant="link" className="p-0 bb-auth-card__link" onClick={() => navigate(ROUTES.HOME)}>{t("Go Back to Home")}


                    </Button>
                  </div>{t("Already have an account?")}

                  {t(" ")}
                  <Link to={ROUTES.LOGIN} className="bb-auth-card__link">{t("Login")}

                  </Link>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>;
}
